// LIBS
import React, {useEffect, useRef, useState} from "react";
import useInterval from "../hooks/useInterval";
import {Button, Switch, message} from "antd";
import {CheckOutlined, CloseOutlined, ReloadOutlined, ThunderboltOutlined} from "@ant-design/icons";
import Countdown, {zeroPad} from 'react-countdown';
import {useTranslation} from "react-i18next";
import event from "../services/event.service";
import {useHpv2EngineContext, useSelectedRaceContext, useIsEngineActiveContext} from "../pages/privateMonitoring";
import positionServer from "../services/position-server.service";
import race from "../services/race.service";
import settings from "../services/settings.service";
import TrackerService from "../services/tracker.service";

const GET_HPV2_NETWORK_INFO_FREQUENCY = 10000;
const RACE_ENGINE_STOP_MAX_DELAY = 30000;

// TODO GBE : Cette liste devrait être dans un endroit commun à FRONT et BACK
const EVENT_TYPES = {
    RACE_ACTIVATE: 'RACE_ACTIVATE',
    RACE_RESET: 'RACE_RESET',
    RACE_START: 'RACE_START',
    RACE_START_CONFIRM: 'RACE_START_CONFIRM',
    RACE_INFO: 'RACE_INFO',
    RACE_RESTART: 'RACE_RESTART',
    RACE_END_PHYSICAL: 'RACE_END_PHYSICAL',
    RACE_END_OFFICIAL: 'RACE_END_OFFICIAL',
    RACE_INFO_UPDATE: 'RACE_INFO_UPDATE',
    COMPETITOR_STATUS_UPDATE: 'COMPETITOR_STATUS_UPDATE',
    COMPETITOR_FUSION_UPDATE: 'COMPETITOR_FUSION_UPDATE',
    FORCE_NETWORK: 'FORCE_NETWORK',
    RACE_TYPE_UPDATE: 'RACE_TYPE_UPDATE',
};

// flat races start in stalls (switch on the left), jump races at the elastic (switch on the right)
const START_TYPES = ['flat-gallop', 'jump-gallop'];

export const MonitoringMapToolbar = React.forwardRef((props, ref) => {
    const {t} = useTranslation();
    const {hpv2Engine} = useHpv2EngineContext();
    const {selectedRace} = useSelectedRaceContext();
    const {isEngineActive} = useIsEngineActiveContext();
    // eslint-disable-next-line
    const [nextStartTime, setNextStartTime] = useState();
    const [lastCommand, setLastCommand] = useState();
    const [activateEnable, setActivateEnable] = useState(false);
    const [restartEnable, setRestartEnable] = useState(false);
    const [endEnable, setEndEnable] = useState(false);
    const [currentSelectedNetwork, setCurrentSelectedNetwork] = useState();
    const [currentSelectedNetworkTechnology, setCurrentSelectedNetworkTechnology] = useState();
    const [isForcedNetworkEnabled, setIsForcedNetworkEnabled] = useState(false);
    const [heatsRunning, setHeatsRunning] = useState(false); // HPv2 engine started outside of a race (heats)
    const [raceType, setRaceType] = useState();
    const [hpv2EngineStarted, setHpv2EngineStarted] = useState(false);
    const [startTypeSwitchEnabled, setStartTypeSwitchEnabled] = useState(false); // MONITORING_START_TYPE_SWITCH (server .env)
    const wasEngineActive = useRef(false);
    const raceEngineStoppingUntil = useRef(0); // ignore HPv2 engine state while it stops after a race end

    // load data ------------------------------------------------------
    const loadSelectedRaceDate = () => {
        race.get(selectedRace).then(race => {
            if (race && race.data) {
                setNextStartTime(race.data.date);
                setRaceType(race.data.engineType);
            }
        });
    };

    const getCurrentHpv2Server = () => {
        // retrieve the current selected server in db to get network info
        positionServer.getSelected(true).then(hpv2Server => {
            if (hpv2Server && hpv2Server.data && hpv2Server.data.networkInfo) {
                setCurrentSelectedNetwork(hpv2Server.data.networkInfo.mcc_mnc);
                setCurrentSelectedNetworkTechnology(hpv2Server.data.networkInfo.network_technology);
                setIsForcedNetworkEnabled(hpv2Server.data.networkInfo.is_forced_network_enabled);
            }
        }).catch(() => {}); // no hpv2 server selected
    }

    // CountDownToRace ----------------------------------------------
    const CountDownToRace = () => {
        return (
            <Countdown
                date={nextStartTime}
                renderer={({hours, minutes, seconds, completed}) => {
                    if (completed) {
                        return t('monitoring.race_started');
                    } else {
                        return t('monitoring.next_start')+ ' ' + zeroPad(hours)+':'+zeroPad(minutes)+':'+zeroPad(seconds)

                    }
                }}
            />
        )
    }

    const sendEvent = (eventType, code, timestamp) => {
        const data = { "type": eventType, "raceUuid": selectedRace}
        if (code) {
            data.code = code;
        }
        if (timestamp) {
            data.timestamp = timestamp;
        }
        event.save(data).then();
    }

    const activate = () => {
        // forbid launching a race while heats are running (HPv2 engine already started outside a race)
        if (heatsRunning) {
            message.warning(t('monitoring.heats_running'));
            return;
        }
        if (activateEnable && !hpv2EngineStarted) {
            sendEvent(EVENT_TYPES.RACE_ACTIVATE, 'B1');
            setLastCommand(t('monitoring.btn_engine_start'));
            setActivateEnable(false);
        }
    };

    const restart = () => {
        if (restartEnable) {
            sendEvent(EVENT_TYPES.RACE_RESTART, 'B2');
            setLastCommand(t('monitoring.btn_race_restart'));
        }
    };

    const end = () => {
        sendEvent(EVENT_TYPES.RACE_END_PHYSICAL);
        setLastCommand(t('monitoring.btn_race_end'));
    };

    const changeRaceType = (value) => {
        sendEvent(EVENT_TYPES.RACE_TYPE_UPDATE, value);
        setRaceType(value);
        setLastCommand(t(`monitoring.race_type.${value}`));
    };

    const forceNetwork = (value) => {
        sendEvent(EVENT_TYPES.FORCE_NETWORK, value?'force':'unforce');
        setIsForcedNetworkEnabled(value);
    };

    useEffect(() => {
        settings.getStartTypeSwitch().then(enabled => setStartTypeSwitchEnabled(enabled));
    }, []);

    useInterval( () => {
        getCurrentHpv2Server();
    },GET_HPV2_NETWORK_INFO_FREQUENCY);

    // detect heats : HPv2 engine started while no race is activated
    useInterval( () => {
        TrackerService.getHpv2EngineState().then(r => {
            const hpv2Started = !!(r && r.data && r.data.state);
            // after a race end, the HPv2 engine takes a few seconds to stop : not heats
            if (!hpv2Started || Date.now() > raceEngineStoppingUntil.current) {
                raceEngineStoppingUntil.current = 0;
            }
            setHpv2EngineStarted(hpv2Started);
            setHeatsRunning(hpv2Started && !isEngineActive && !raceEngineStoppingUntil.current);
        });
    }, 2000);

    useEffect(() => {
        if (selectedRace) {
            if (!isEngineActive) {
                setActivateEnable(true);
            }
            loadSelectedRaceDate();
        } else {
            setActivateEnable(false);
            setNextStartTime(null);
            setRaceType(undefined);
        }
        getCurrentHpv2Server();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRace]);

    useEffect(() => {
        if (wasEngineActive.current && !isEngineActive) {
            raceEngineStoppingUntil.current = Date.now() + RACE_ENGINE_STOP_MAX_DELAY;
            setHeatsRunning(false);
        }
        wasEngineActive.current = isEngineActive;
        if (selectedRace) {
            setActivateEnable(!isEngineActive);
        }
        setRestartEnable(isEngineActive);
        setEndEnable(isEngineActive);
        // if (nextStartTime) {
        //     let next = new Date().setHours(nextStartTime.split(':')[0], nextStartTime.split(':')[1])
        //     // set_countdown_next_start_time(next);
        // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEngineActive]);

    // render ---------------------------------------------------------
    return (
        <div className="map_pilotage_toolbar">
            <div className="map_pilotage_toolbar_header">
                {nextStartTime?
                <span className="next_start">
                  <CountDownToRace/>
                </span>
                :
                <span className="next_start_hidden">next_start</span>
                }
            </div>
            <div className="map_pilotage_toolbar_button_wrapper">
                {hpv2Engine ? (
                <div className="tlb-btn-wrapper">
                    <Button
                        className={`tlb-btn ${(!activateEnable)? 'disable':''}`}
                        onClick={() => {activate(false);}}
                        shape="circle"
                        icon={<ThunderboltOutlined/>}>
                    </Button>
                    <span>{t('monitoring.btn_engine_stop')}</span>
                </div>
                ) : (
                <div className="tlb-btn-wrapper">
                    <Button
                        className={`tlb-btn ${(!activateEnable || hpv2EngineStarted)? 'disable':''}`}
                        onClick={() => {activate(true);}}
                        shape="circle"
                        icon={<ThunderboltOutlined/>}>
                    </Button>
                    <span className={heatsRunning ? 'heats_running_blink' : ''}>{heatsRunning ? t('monitoring.heats_running_short') : t('monitoring.btn_engine_start')}</span>
                </div>
                )}

                {/* RACE_TYPE_UPDATE : switch between flat (stalls start) and jump (elastic start) */}
                {startTypeSwitchEnabled && START_TYPES.includes(raceType) ? (
                <div className="tlb-btn-wrapper">
                    <div className="start_type_switch">
                        <span className={raceType === START_TYPES[0] ? 'current' : ''}>{t('monitoring.start_stalls')}</span>
                        <Switch
                            checked={raceType === START_TYPES[1]}
                            disabled={!isEngineActive}
                            onChange={(elastic) => changeRaceType(START_TYPES[elastic ? 1 : 0])}>
                        </Switch>
                        <span className={raceType === START_TYPES[1] ? 'current' : ''}>{t('monitoring.start_elastic')}</span>
                    </div>
                    <span>{t('monitoring.start_type')}</span>
                </div>
                ) : null}

                {/* RACE_RESTART */}
                <div className="tlb-btn-wrapper">
                    <Button
                        className={`tlb-btn ${(!restartEnable)? 'disable':''}`}
                        onClick={() => {restart();}}
                        shape="circle"
                        icon={<ReloadOutlined/>}>
                    </Button>
                    <span>{t('monitoring.btn_race_restart')}</span>
                </div>

                {/* RACE_END */}
                <div className="tlb-btn-wrapper">
                    <Button
                        className={`tlb-btn ${(!endEnable)? 'disable':''}`}
                        onClick={() => {end()}}
                        shape="circle"
                        icon={<CheckOutlined/>}>
                    </Button>
                    <span>{t('monitoring.btn_race_end')}</span>
                </div>
            </div>

            <div className="map_pilotage_toolbar_footer">
                <span className="next_start">{t('monitoring.last_command')} : <span>{lastCommand}</span></span>
                {currentSelectedNetwork && selectedRace?
                <span id="force-network-switch-container">
                    <label htmlFor={'force-network-switch'}>{`${t('monitoring.force_network')} ${currentSelectedNetwork} : ${currentSelectedNetworkTechnology}G    `}</label>
                    <Switch
                        id="force-network-switch"
                        checked={isForcedNetworkEnabled}
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                        onChange={(v) => { forceNetwork(v) }}>
                    </Switch>
                </span>:''
                }
            </div>
        </div>
    )
})

export default MonitoringMapToolbar;